/**
 * SubagentChildrenList — the threads under a conversation (conversation-members spec): each member that was asked
 * something works on a thread of its own under the conversation, and a debug session started from it runs on one too.
 * Listed in the context rail so every thread is one click away, beside the "Open thread" link on each answer. Nothing
 * is listed while there are none. Demo page: /chats (context column), in a catalogue `Card`.
 */
import { useQuery } from '@tanstack/react-query';
import { Bot, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { listConversationsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { Callout } from '../ui/Callout.js';
import { Card } from '../ui/Card.js';
import { SkeletonList } from '../ui/Skeleton.js';

interface SubagentChildrenListProps {
  parentConversationId: string;
}

export function SubagentChildrenList({ parentConversationId }: SubagentChildrenListProps) {
  const { data, isLoading, isError } = useQuery({
    ...listConversationsOptions({ query: { parentConversationId } }),
    enabled: Boolean(parentConversationId),
    select: (response) => response.data,
  });

  if (isLoading) return <SkeletonList count={2} label="Loading threads" />;
  if (isError) return <Callout tone="danger">Could not load this conversation's threads.</Callout>;

  const children = data ?? [];
  if (children.length === 0) return null;

  return (
    <Card title={`Threads (${children.length})`}>
      <ul className="space-y-1.5">
        {children.map((child) => (
          <li key={child.id} className="flex items-start gap-1.5 text-xs">
            <Bot size={12} className="mt-0.5 shrink-0 text-text-tertiary" aria-hidden />
            <div className="min-w-0 flex-1">
              <Link
                to={`/conversations/${String(child.id)}`}
                className="block truncate text-primary-500 hover:underline"
              >
                {child.topic}
              </Link>
              <span className="text-text-tertiary">{child.templateSlug}</span>
            </div>
            <Link
              to={`/conversations/${String(child.id)}`}
              className="mt-0.5 shrink-0 text-text-tertiary hover:text-title-50"
              aria-label={`Open ${child.topic}`}
            >
              <ExternalLink size={12} aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
